import { Accordion } from "@components/Accordion";
import { Checkbox, CheckboxSize } from "@components/Checkbox";
import { Icon, IconDisplay, IconSize } from "@components/Icon";
import { DonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import { DonateFormType } from "@components/donate/DonateV3/types";
import styled from "@emotion/styled";
import { Big } from "big.js";
import React, { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import {
  Currency,
  DonationFlowPaymentOption,
} from "@every.org/common/src/entity/types";

import { colorCssVars } from "src/theme/color";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { FontWeight, TextSize, textSizeCss } from "src/theme/text";
import { displayCurrencyValueInUserLocale } from "src/utility/currency";

export interface CoverCostsProps {
  form: UseFormReturn<DonateFormType>;
  formContext: DonateFormContext;
  platformCostPercent: number;
  uiVariant: string;
  paymentOption: DonationFlowPaymentOption;
}

function calcCoverCostsAmount({
  amount,
  platformCostPercent,
  paymentOption,
  isInternational,
}: {
  amount: Big;
  platformCostPercent: number;
  paymentOption: DonationFlowPaymentOption;
  isInternational: boolean;
}): Big {
  let percentCost = 0.0;
  let fixedCost = 0.0;
  let percentInternationalCost = 0.0;
  switch (paymentOption) {
    case DonationFlowPaymentOption.PAYPAL:
    case DonationFlowPaymentOption.VENMO:
      percentCost = 1.99;
      percentInternationalCost = isInternational ? 1.5 : 0.0;
      fixedCost = 0.49;
      break;
    case DonationFlowPaymentOption.CREDIT_CARD:
    case DonationFlowPaymentOption.PAYMENT_REQUEST:
      percentCost = 2.2;
      percentInternationalCost = isInternational ? 1.0 : 0.0;
      fixedCost = 0.3;
      break;
  }

  return amount
    .times(
      new Big(platformCostPercent)
        .plus(percentCost)
        .plus(percentInternationalCost)
    )
    .div(100)
    .plus(fixedCost);
}

function formatAmount(amount: Big, currency: Currency): string {
  return displayCurrencyValueInUserLocale({
    currencyValue: { amount, currency },
  });
}

const StyledAccordion = styled(Accordion)`
  .AccordionStyledHeader {
    ${horizontalStackCss.xxxs};
    justify-content: flex-start;
  }

  .AccordionStyledHeader--title,
  .AccordionStyledHeader--title_open {
    ${textSizeCss[TextSize.xs]};
    font-weight: ${FontWeight.MEDIUM};
    color: var(${colorCssVars.text.body});
  }
`;

const CheckboxRow = styled.div`
  ${horizontalStackCss.xs};
  align-items: center;
`;

const ThankYouBadge = styled.span`
  ${horizontalStackCss.xxxs};
  align-items: center;
  ${textSizeCss[TextSize.xs]};
  line-height: 1rem;
  font-weight: ${FontWeight.MEDIUM};
  color: var(${colorCssVars.accent.small});
  background-color: #e5f2f0;
  border-radius: ${spacing.xs};
  padding: 4px ${spacing.xs};
  white-space: nowrap;
`;

const FaqText = styled.div`
  ${textSizeCss[TextSize.xs]};
`;

const EditSummaryRow = styled.div`
  ${horizontalStackCss.xs};
  align-items: center;
  ${textSizeCss[TextSize.s]};
  color: var(${colorCssVars.text.body});
`;

const EditLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  ${textSizeCss[TextSize.s]};
  font-weight: ${FontWeight.MEDIUM};
  color: var(${colorCssVars.accent.large});
  cursor: pointer;
  text-decoration: underline;
`;

const EditBox = styled.div`
  ${verticalStackCss.s};
  border: 1px solid var(${colorCssVars.dividerSoft});
  border-radius: ${spacing.xs};
  padding: ${spacing.m};
`;

const DonationAmountDisplay = styled.span`
  ${textSizeCss[TextSize.xxl]};
  font-weight: ${FontWeight.BOLD};
  color: var(${colorCssVars.text.body});
`;

function FaqDescription({
  donationAmount,
  coverCostsAmount,
  coverCostsChecked,
  currency,
}: {
  donationAmount: Big;
  coverCostsAmount: Big;
  coverCostsChecked: boolean;
  currency: Currency;
}) {
  const taxDeductibleAmount = coverCostsChecked
    ? donationAmount.add(coverCostsAmount)
    : donationAmount;
  const totalStr = displayCurrencyValueInUserLocale({
    currencyValue: { amount: taxDeductibleAmount, currency },
  });
  return (
    <FaqText>
      Covering costs ensures payment processing fees don&#39;t come out of your
      donation and helps sustain Every.org, the nonprofit powering this
      fundraising platform. 100% of your {totalStr} donation is tax-deductible
      to the extent allowed by US law.
    </FaqText>
  );
}

function CheckboxVariant({
  form,
  donationAmount,
  coverCostsAmount,
  currency,
}: {
  form: UseFormReturn<DonateFormType>;
  donationAmount: Big;
  coverCostsAmount: Big;
  currency: Currency;
}) {
  const checked = form.watch("coverCostsChecked") ?? true;
  const displayStr = formatAmount(coverCostsAmount, currency);

  return (
    <div css={verticalStackCss.m}>
      <DonationAmountDisplay>
        {formatAmount(donationAmount, currency)}
      </DonationAmountDisplay>
      <CheckboxRow>
        <Checkbox
          size={CheckboxSize.LARGE}
          css={[horizontalStackCss.xs]}
          data-tname="coverCostsCheckbox"
          checked={checked}
          onChange={() => {
            form.setValue("coverCostsChecked", !checked);
          }}
        >
          Add {displayStr} to cover costs
        </Checkbox>
        {checked && (
          <ThankYouBadge>
            <Icon
              iconImport={() => import("@components/Icon/icons/ThumbsUpIcon")}
              size={IconSize.X_SMALL}
              display={IconDisplay.CURRENT_COLOR}
            />
            <span>Thank you</span>
          </ThankYouBadge>
        )}
      </CheckboxRow>
      <StyledAccordion
        dataList={[
          {
            id: "cover-costs-faq",
            title: "What are the costs?",
            description: (
              <FaqDescription
                donationAmount={donationAmount}
                coverCostsAmount={coverCostsAmount}
                coverCostsChecked={checked}
                currency={currency}
              />
            ),
          },
        ]}
      />
    </div>
  );
}

function EditVariant({
  form,
  donationAmount,
  coverCostsAmount,
  currency,
}: {
  form: UseFormReturn<DonateFormType>;
  donationAmount: Big;
  coverCostsAmount: Big;
  currency: Currency;
}) {
  const checked = form.watch("coverCostsChecked") ?? true;
  const [editOpen, setEditOpen] = useState(false);
  const displayStr = formatAmount(coverCostsAmount, currency);

  return (
    <div css={verticalStackCss.m}>
      <DonationAmountDisplay>
        {formatAmount(donationAmount, currency)}
      </DonationAmountDisplay>
      <EditSummaryRow>
        {checked ? (
          <span>+ {displayStr} to cover costs</span>
        ) : (
          <span style={{ color: `var(${colorCssVars.text.secondary})` }}>
            Costs not covered
          </span>
        )}
        <EditLink type="button" onClick={() => setEditOpen((o) => !o)}>
          Edit
        </EditLink>
      </EditSummaryRow>
      {editOpen && (
        <EditBox>
          <FaqDescription
            donationAmount={donationAmount}
            coverCostsAmount={coverCostsAmount}
            coverCostsChecked={checked}
            currency={currency}
          />
          <CheckboxRow>
            <Checkbox
              size={CheckboxSize.LARGE}
              css={[horizontalStackCss.xs]}
              data-tname="coverCostsEditCheckbox"
              checked={checked}
              onChange={() => {
                form.setValue("coverCostsChecked", !checked);
              }}
            >
              I want to cover costs
            </Checkbox>
          </CheckboxRow>
        </EditBox>
      )}
    </div>
  );
}

export const CoverCosts = ({
  form,
  formContext,
  platformCostPercent,
  paymentOption,
  uiVariant,
}: CoverCostsProps) => {
  const { amountBig } = formContext;
  const currency = form.watch("currency");
  const coverCostsAmount = calcCoverCostsAmount({
    amount: amountBig,
    platformCostPercent,
    paymentOption,
    isInternational: false,
  });

  // Initialize to checked on mount (or when donation amount changes).
  useEffect(() => {
    const current = form.getValues("coverCostsChecked");
    if (current === undefined) {
      form.setValue("coverCostsChecked", true);
    }
    // Keep coverCostsAmount in sync with donation amount
    form.setValue("coverCostsAmount", coverCostsAmount.toFixed(2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountBig]);

  if (uiVariant === "edit") {
    return (
      <EditVariant
        form={form}
        donationAmount={amountBig}
        coverCostsAmount={coverCostsAmount}
        currency={currency}
      />
    );
  }

  if (uiVariant === "checkbox") {
    return (
      <CheckboxVariant
        form={form}
        donationAmount={amountBig}
        coverCostsAmount={coverCostsAmount}
        currency={currency}
      />
    );
  }

  return null;
};
